import type { Dosage } from '@medplum/fhirtypes';

/** Bare dose count + form — e.g. "2 tablet" — composed from the structured dose/method fields.
 * Returns `undefined` when there is no structured dose, so callers fall back to `dosage.text`. */
const getStructuredDoseText = (dosage: Dosage | undefined): string | undefined => {
  const doseQuantity = dosage?.doseAndRate?.[0]?.doseQuantity;
  if (doseQuantity?.value === undefined) return undefined;
  const form = dosage?.method?.text ?? dosage?.method?.coding?.[0]?.display;
  const unit = doseQuantity.unit;
  const suffix = form ?? unit;
  return suffix ? `${doseQuantity.value} ${suffix}` : `${doseQuantity.value}`;
};

/** The dose shown on its own (Records list/detail "Dosage" field, where route and frequency have
 * their own fields). Prefers the structured dose ("2 tablet") over `dosage.text`, because a
 * Liza-authored statement now stores the full prescriber sig ("Take 2 tablet by mouth twice
 * daily") in `dosage.text` — using that here would duplicate the adjacent Route/Frequency fields.
 * Ingested records that carry only `dosage.text` fall back to it verbatim. */
export const getMedicationDosageText = (dosage: Dosage | undefined): string | undefined => {
  return (getStructuredDoseText(dosage) ?? dosage?.text?.trim()) || undefined;
};

/** Route phrases that already read as a prepositional phrase, so prefixing "by" would double it
 * (e.g. "by mouth" → "by by mouth", "each eye", "per rectum"). */
const ROUTE_PREPOSITION = /^(by|per|each|via|in|into|on)\b/i;

/** Render a route as a "<by> <route>" clause, prepending "by" only when the stored text isn't
 * already a prepositional phrase. Route text varies by entry path — the route ValueSet display is
 * a noun ("Oral route") while free-text/chat entry stores a phrase ("by mouth") — so a fixed "by"
 * prefix produces "by by mouth" for the latter. */
const formatRouteClause = (route: Dosage['route']): string | undefined => {
  const routeText = route?.text ?? route?.coding?.[0]?.display;
  if (!routeText) return undefined;
  return ROUTE_PREPOSITION.test(routeText) ? routeText : `by ${routeText}`;
};

/** Compose the short "<dose> by <route>" subtitle shown on care-plan / day-view medication task
 * rows. Same structured-first dose as {@link getMedicationDosageText}, plus the route appended
 * (these rows have no separate route field; they render the schedule on its own line, so reusing
 * the full sig in `dosage.text` would double the route and frequency). */
export const getMedicationDescriptionText = (dosage: Dosage | undefined): string | null => {
  const doseText = getMedicationDosageText(dosage);
  const routeClause = formatRouteClause(dosage?.route);
  return [doseText, routeClause].filter(Boolean).join(' ') || null;
};
